/**
 * Project-member management, rendered as a section in the cloud-mode
 * Project settings dialog (Phase 4).
 *
 * Two ways to grant project access:
 *   • Add an existing org member directly (any maintainer can do this).
 *   • Invite someone new by email with a project role baked in — this
 *     creates an org invitation carrying a project grant, so it requires
 *     org admin; the server 403s otherwise and we surface that inline.
 *
 * `canManage` (the caller's canManageMembers capability) hides the
 * mutating controls; everyone who can read the project sees the roster.
 * The server enforces all of this regardless of what the UI shows.
 */

import { useEffect, useState } from 'react';

import {
  type MemberRow,
  type OrgApi,
  OrgApiError,
  type ProjectMemberRow,
  type ProjectRole,
} from '../data/orgApi.js';

import { Spinner } from './Spinner.js';

const PROJECT_ROLES: ProjectRole[] = ['maintainer', 'developer', 'writer', 'viewer'];

interface Props {
  api: OrgApi;
  projectId: string;
  canManage: boolean;
}

export function ProjectMembersPanel({ api, projectId, canManage }: Props): JSX.Element {
  const [members, setMembers] = useState<ProjectMemberRow[] | null>(null);
  const [orgMembers, setOrgMembers] = useState<MemberRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const [addUserId, setAddUserId] = useState('');
  const [addRole, setAddRole] = useState<ProjectRole>('writer');

  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<ProjectRole>('writer');
  const [inviteMsg, setInviteMsg] = useState<string | null>(null);

  const refresh = async (): Promise<void> => {
    try {
      const [pm, om] = await Promise.all([
        api.listProjectMembers(projectId),
        // The "add existing member" picker needs the org roster, but only
        // managers can both see it and act on it.
        canManage ? api.listMembers() : Promise.resolve<MemberRow[]>([]),
      ]);
      setMembers(pm);
      setOrgMembers(om);
      setError(null);
    } catch (err) {
      setError(asMessage(err));
    }
  };

  useEffect(() => {
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  const assignedIds = new Set((members ?? []).map((m) => m.userId));
  // Owners/admins are implicit super-users — no point assigning them a
  // project role — so only plain members who aren't already on the
  // project are candidates to add.
  const candidates = orgMembers.filter((m) => m.role === 'member' && !assignedIds.has(m.userId));

  const run = async (fn: () => Promise<void>): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      await fn();
    } catch (err) {
      setError(asMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const handleAdd = (): Promise<void> =>
    run(async () => {
      if (!addUserId) return;
      await api.addProjectMember(projectId, { userId: addUserId, role: addRole });
      setAddUserId('');
      await refresh();
    });

  const handleRoleChange = (userId: string, role: ProjectRole): Promise<void> =>
    run(async () => {
      await api.updateProjectMemberRole(projectId, userId, role);
      await refresh();
    });

  const handleRemove = (userId: string): Promise<void> =>
    run(async () => {
      await api.removeProjectMember(projectId, userId);
      await refresh();
    });

  const handleInvite = (): Promise<void> =>
    run(async () => {
      const email = inviteEmail.trim();
      if (!email) return;
      setInviteMsg(null);
      await api.inviteMember({
        email,
        role: 'member',
        projectGrants: [{ projectId, role: inviteRole }],
      });
      setInviteEmail('');
      setInviteMsg(`Invitation sent to ${email}.`);
    });

  if (members === null && !error) {
    return <Spinner label="Loading members…" />;
  }

  return (
    <div className="project-members">
      {error && (
        <p className="project-settings-error" role="alert">
          {error}
        </p>
      )}

      {(members ?? []).length === 0 ? (
        <p className="project-settings-hint">
          No project members yet. Org owners and admins always have full access.
        </p>
      ) : (
        <table className="project-members-table">
          <thead>
            <tr>
              <th>Member</th>
              <th>Role</th>
              {canManage && <th aria-label="Actions" />}
            </tr>
          </thead>
          <tbody>
            {(members ?? []).map((m) => (
              <tr key={m.userId}>
                <td>{m.name ?? m.email}</td>
                <td>
                  {canManage ? (
                    <select
                      className="input"
                      value={m.role}
                      disabled={busy}
                      aria-label={`Role for ${m.email}`}
                      onChange={(e) =>
                        void handleRoleChange(m.userId, e.target.value as ProjectRole)
                      }
                    >
                      {PROJECT_ROLES.map((r) => (
                        <option key={r} value={r}>
                          {r}
                        </option>
                      ))}
                    </select>
                  ) : (
                    m.role
                  )}
                </td>
                {canManage && (
                  <td>
                    <button
                      type="button"
                      className="landing-button-text"
                      disabled={busy}
                      onClick={() => void handleRemove(m.userId)}
                    >
                      Remove
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {canManage && (
        <>
          <div className="project-members-add">
            <span className="landing-label">Add an org member</span>
            <div className="project-members-add-row">
              <select
                className="input"
                value={addUserId}
                disabled={busy || candidates.length === 0}
                aria-label="Org member to add"
                onChange={(e) => setAddUserId(e.target.value)}
              >
                <option value="">
                  {candidates.length ? 'Select a member…' : 'No org members to add'}
                </option>
                {candidates.map((m) => (
                  <option key={m.userId} value={m.userId}>
                    {m.name ?? m.email}
                  </option>
                ))}
              </select>
              <select
                className="input"
                value={addRole}
                disabled={busy}
                aria-label="Role for new member"
                onChange={(e) => setAddRole(e.target.value as ProjectRole)}
              >
                {PROJECT_ROLES.map((r) => (
                  <option key={r} value={r}>
                    {r}
                  </option>
                ))}
              </select>
              <button
                type="button"
                className="landing-button-primary"
                disabled={busy || !addUserId}
                onClick={() => void handleAdd()}
              >
                Add
              </button>
            </div>
          </div>

          <div className="project-members-invite">
            <span className="landing-label">Invite someone new by email</span>
            <div className="project-members-add-row">
              <input
                className="input"
                type="email"
                placeholder="name@example.com"
                value={inviteEmail}
                disabled={busy}
                onChange={(e) => {
                  setInviteEmail(e.target.value);
                  setInviteMsg(null);
                }}
              />
              <select
                className="input"
                value={inviteRole}
                disabled={busy}
                aria-label="Role for invitee"
                onChange={(e) => setInviteRole(e.target.value as ProjectRole)}
              >
                {PROJECT_ROLES.map((r) => (
                  <option key={r} value={r}>
                    {r}
                  </option>
                ))}
              </select>
              <button
                type="button"
                className="landing-button-primary"
                disabled={busy || inviteEmail.trim() === ''}
                onClick={() => void handleInvite()}
              >
                Invite
              </button>
            </div>
            {inviteMsg && <p className="project-settings-hint">{inviteMsg}</p>}
            <p className="project-settings-hint">
              They join the org as a member with this project role. Inviting new people requires org
              admin.
            </p>
          </div>
        </>
      )}
    </div>
  );
}

function asMessage(err: unknown): string {
  if (err instanceof OrgApiError) return err.message;
  return err instanceof Error ? err.message : 'Something went wrong.';
}
